import { supabase } from '../lib/supabase';

export interface OrganizationCredits {
  id: string;
  organization_id: string;
  credit_balance: number;
  total_credits_purchased: number;
  total_credits_used: number;
  created_at: string;
  updated_at: string;
}

export interface CreditTransaction {
  id: string;
  organization_id: string;
  user_id: string;
  transaction_type: 'purchase' | 'usage' | 'refund';
  amount: number;
  description: string;
  service_type?: string;
  reference_id?: string;
  balance_before: number;
  balance_after: number;
  created_at: string;
}

export interface CreditPackage {
  id: string;
  name: string;
  description: string;
  credit_amount: number;
  price_aud: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export class CreditsService {
  // Get organization's credit balance and details
  static async getOrganizationCredits(organizationId: string): Promise<OrganizationCredits | null> {
    try {
      const { data, error } = await supabase
        .from('organization_credits')
        .select('*')
        .eq('organization_id', organizationId)
        .single();

      if (error) {
        // If no record exists, initialize it
        if (error.code === 'PGRST116') {
          await this.initializeOrganizationCredits(organizationId);
          return await this.getOrganizationCredits(organizationId);
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error fetching organization credits:', error);
      throw error;
    }
  }

  // Initialize credits for an organization
  static async initializeOrganizationCredits(organizationId: string): Promise<void> {
    try {
      const { error } = await supabase.rpc('initialize_organization_credits', {
        org_id: organizationId
      });

      if (error) throw error;
    } catch (error) {
      console.error('Error initializing organization credits:', error);
      throw error;
    }
  }

  // Add credits to organization
  static async addCreditsToOrganization(
    organizationId: string,
    userId: string,
    creditAmount: number,
    description: string = 'Credit purchase'
  ): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('add_credits_to_organization', {
        org_id: organizationId,
        user_id: userId,
        credit_amount: creditAmount,
        description: description
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding credits to organization:', error);
      throw error;
    }
  }

  // Check if organization has sufficient credits
  static async checkSufficientCredits(organizationId: string, requiredAmount: number): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('check_sufficient_credits', {
        org_id: organizationId,
        required_amount: requiredAmount
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error checking sufficient credits:', error);
      throw error;
    }
  }

  // Deduct credits for service usage
  static async deductCreditsForService(
    organizationId: string,
    userId: string,
    creditAmount: number,
    serviceType: string,
    description: string,
    referenceId?: string
  ): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('deduct_credits_for_service', {
        org_id: organizationId,
        user_id: userId,
        credit_amount: creditAmount,
        service_type: serviceType,
        description: description,
        reference_id: referenceId
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error deducting credits for service:', error);
      throw error;
    }
  }

  // Get credit transaction history
  static async getCreditTransactions(
    organizationId: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<CreditTransaction[]> {
    try {
      const { data, error } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching credit transactions:', error);
      throw error;
    }
  }

  // Get available credit packages
  static async getCreditPackages(): Promise<CreditPackage[]> {
    try {
      const { data, error } = await supabase
        .from('credit_packages')
        .select('*')
        .eq('is_active', true)
        .order('credit_amount', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching credit packages:', error);
      throw error;
    }
  }

  // Service cost constants
  static readonly SERVICE_COSTS = {
    PPSR_CHECK: 10.0,
    VALUATION_CHECK: 10.0
  } as const;

  // Helper method to get service cost
  static getServiceCost(serviceType: 'ppsr_check' | 'valuation_check'): number {
    switch (serviceType) {
      case 'ppsr_check':
        return this.SERVICE_COSTS.PPSR_CHECK;
      case 'valuation_check':
        return this.SERVICE_COSTS.VALUATION_CHECK;
      default:
        return 0;
    }
  }

  // Check and deduct credits for PPSR check
  static async processCheckCredits(
    organizationId: string,
    userId: string,
    serviceType: 'ppsr_check' | 'valuation_check',
    referenceId?: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const cost = this.getServiceCost(serviceType);
      const serviceName = serviceType === 'ppsr_check' ? 'PPSR Check' : 'Valuation Check';

      // Check if sufficient credits
      const hasSufficientCredits = await this.checkSufficientCredits(organizationId, cost);

      if (!hasSufficientCredits) {
        return {
          success: false,
          message: `Insufficient credits. ${serviceName} requires ${cost} credits.`
        };
      }

      // Deduct credits
      const success = await this.deductCreditsForService(
        organizationId,
        userId,
        cost,
        serviceType,
        `${serviceName} - ${new Date().toLocaleDateString()}`,
        referenceId
      );

      if (!success) {
        return {
          success: false,
          message: `Failed to deduct credits for ${serviceName}.`
        };
      }

      return {
        success: true,
        message: `${serviceName} completed. ${cost} credits deducted.`
      };

    } catch (error) {
      console.error(`Error processing ${serviceType} credits:`, error);
      return {
        success: false,
        message: 'An error occurred while processing credits.'
      };
    }
  }
}